import { t } from "@lingui/macro";
import { GlobeSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import type { ReactNode } from "react";

/**
 * BasicsForm — flat (non-repeatable) personal-info form (Apply Edge builder v2).
 *
 * Fully controlled: pass `value` + `onChange(patch)`. The profile picture upload
 * area is a slot (`pictureSlot`) the parent wires. Social links are a controlled
 * list with a platform select + URL field per row.
 *
 * This flat-form pattern also serves any other non-repeatable section.
 */
export type BasicsValue = {
  name: string;
  headline: string;
  email: string;
  phone: string;
  location: string;
  url: string;
};

export type SocialLink = {
  id: string;
  platform: string;
  url: string;
};

export type BasicsFormProps = {
  value: BasicsValue;
  onChange: (patch: Partial<BasicsValue>) => void;
  pictureSlot?: ReactNode;
  socials?: SocialLink[];
  platformOptions?: { value: string; label: string }[];
  onAddSocial?: () => void;
  onChangeSocial?: (id: string, patch: Partial<SocialLink>) => void;
  onRemoveSocial?: (id: string) => void;
};

const inputCls =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"; // eslint-disable-line lingui/no-unlocalized-strings

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <span className="mb-1.5 block font-mono text-[10px] text-muted-foreground">{children}</span>
);

export const BasicsForm = ({
  value,
  onChange,
  pictureSlot,
  socials = [],
  platformOptions = [],
  onAddSocial,
  onChangeSocial,
  onRemoveSocial,
}: BasicsFormProps) => {
  const fields: {
    key: keyof BasicsValue;
    label: string;
    placeholder: string;
    type?: string;
    full?: boolean;
  }[] = [
    { key: "name", label: t`Full name`, placeholder: t`Priya Kapoor` },
    { key: "headline", label: t`Headline`, placeholder: t`Payments Product Manager` },
    { key: "email", label: t`Email`, placeholder: t`you@email.com`, type: "email" },
    { key: "phone", label: t`Phone`, placeholder: t`+49 …`, type: "tel" },
    { key: "location", label: t`Location`, placeholder: t`Berlin, DE` },
    { key: "url", label: t`Website`, placeholder: t`yoursite.com`, type: "url" },
  ];

  return (
    <div className="flex flex-col gap-5">
      {/* Picture + name block */}
      {pictureSlot ? (
        <div className="flex items-center gap-4">
          <div className="shrink-0">{pictureSlot}</div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {t`Add a profile picture. A square photo at least 256px works best.`}
          </p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {fields.map((field) => (
          <label key={field.key} className={field.full ? "sm:col-span-2" : undefined}>
            <FieldLabel>{field.label}</FieldLabel>
            <input
              className={inputCls}
              type={field.type ?? "text"}
              value={value[field.key]}
              placeholder={field.placeholder}
              aria-label={field.label}
              onChange={(e) => {
                onChange({ [field.key]: e.target.value });
              }}
            />
          </label>
        ))}
      </div>

      {/* Social links */}
      <div>
        <FieldLabel>{t`Social links`}</FieldLabel>
        <div className="flex flex-col gap-2">
          {socials.map((social) => (
            <div key={social.id} className="grid grid-cols-[120px_1fr_auto] gap-2">
              <select
                className={inputCls}
                value={social.platform}
                aria-label={t`Platform`}
                onChange={(e) => onChangeSocial?.(social.id, { platform: e.target.value })}
              >
                {platformOptions.length === 0 ? (
                  <option value={social.platform}>{social.platform}</option>
                ) : null}
                {platformOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="relative">
                <GlobeSimpleIcon
                  size={15}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  className={`${inputCls} pl-8`}
                  type="url"
                  value={social.url}
                  placeholder={t`https://…`}
                  aria-label={t`URL`}
                  onChange={(e) => onChangeSocial?.(social.id, { url: e.target.value })}
                />
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="h-9 w-9 text-muted-foreground hover:text-rose-500"
                aria-label={t`Remove link`}
                onClick={() => onRemoveSocial?.(social.id)}
              >
                <TrashIcon size={15} />
              </Button>
            </div>
          ))}

          <Button
            variant="ghost"
            className="justify-start gap-2 text-primary hover:text-primary"
            aria-label={t`Add social link`}
            onClick={onAddSocial}
          >
            <PlusIcon size={15} />
            {t`Add link`}
          </Button>
        </div>
      </div>
    </div>
  );
};
